import * as React from 'react';
import {
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Skeleton,
  Grid,
  Dialog,
  Stack,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { centered_flex_box } from '../../app/components/Styles';
import {
  marketingColors,
  sectionLabelSx,
  sectionTitleSx,
  marketingFonts,
  eventSurfacePaperSx,
} from '../../app/components/tickitMarketingTheme';
import { MarketingPage, MarketingCard, MarketingButton } from '../../app/components/marketing';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from "@mui/icons-material/Payment";
import { getCart, clearCart, removeFromCart, checkout } from '../../app/store/actions/userActions';
import { Image } from 'antd';
import ActionDialog from '../../app/components/ActionDialog';
import MuiPhoneNumber from 'material-ui-phone-number';
import { motion } from 'framer-motion';

export const Cart = ({ getCart, clearCart, removeFromCart, cart, isLoading, checkout }) => {
  const navigate = useNavigate();
  const [clearDialog, setClearDialog] = React.useState(false);
  const [checkoutDialog, setCheckoutDialog] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState("Card");
  const convenienceFees = ((cart?.totalPrice * 0.0275 + 5) * 1.14).toFixed(2);

  React.useEffect(() => {
    getCart({});
  }, []);

  const handleClearCart = () => {
    clearCart({});
    setClearDialog(false);
  };

  const handleCheckout = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    checkout({ paymentMethod, mobile: data.get('mobile') });
    setCheckoutDialog(false);
  };

  return (
    <MarketingPage maxWidth="lg">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography sx={sectionLabelSx}>Account</Typography>
          <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '1.75rem', md: '2.15rem' } }}>
            Cart
          </Typography>
        </Box>

        <MarketingCard>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: '1.25rem', color: marketingColors.gray900 }}>
                Items
              </Typography>
              {cart?.items?.length > 0 && (
                <MarketingButton variant="danger" onClick={() => setClearDialog(true)}>
                  Remove All
                </MarketingButton>
              )}
            </Box>

            {isLoading ? (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={120} />
                <Skeleton variant="rounded" height={120} />
              </Stack>
            ) : cart?.items?.length > 0 ? (
              <Stack spacing={2}>
                {cart.items.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      p: 2,
                      ...eventSurfacePaperSx,
                      '&:hover': { borderColor: marketingColors.p300, boxShadow: '0 4px 16px rgba(70,16,155,0.1)' },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                          onClick={() => navigate(`/event/${item.itemId}`)}
                        >
                          <Image
                            src={item.event.image || "https://tickit-media.s3.eu-central-1.amazonaws.com/images/Template.png"}
                            width={100}
                            height={100}
                            style={{ borderRadius: 8, maxWidth: '100%', height: 'auto' }}
                          />
                          <Box>
                            <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, color: marketingColors.gray900 }}>
                              {item.event.title}
                            </Typography>
                            <Typography sx={{ fontFamily: marketingFonts.body, fontSize: '0.875rem', color: marketingColors.gray500 }}>
                              Category: {item.category}
                            </Typography>
                            <Typography sx={{ fontFamily: marketingFonts.body, fontSize: '0.875rem', color: marketingColors.gray500 }}>
                              {item.quantity} ticket(s)
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, color: marketingColors.p700 }}>
                            {item.price} EGP
                          </Typography>
                          <IconButton
                            onClick={() => removeFromCart({ itemId: item._id })}
                            sx={{ color: 'error.main', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box sx={{ ...centered_flex_box, flexDirection: 'column', py: 4 }}>
                <ShoppingCartIcon sx={{ fontSize: 60, color: marketingColors.gray400, mb: 2 }} />
                <Typography sx={{ fontFamily: marketingFonts.body, color: marketingColors.gray500 }}>
                  Your cart is empty
                </Typography>
              </Box>
            )}

            {cart?.items?.length > 0 && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  backgroundColor: marketingColors.p50,
                  border: `1px solid ${marketingColors.p200}`,
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontFamily: marketingFonts.body, color: marketingColors.gray600 }}>Subtotal</Typography>
                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, color: marketingColors.gray900 }}>
                      {cart?.totalPrice} EGP
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontFamily: marketingFonts.body, color: marketingColors.gray600 }}>Convenience Fees</Typography>
                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, color: marketingColors.gray900 }}>
                      {convenienceFees} EGP
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, color: marketingColors.gray900 }}>Total</Typography>
                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: '1.25rem', color: marketingColors.p700 }}>
                      {cart?.totalPrice + parseFloat(convenienceFees)} EGP
                    </Typography>
                  </Box>
                  <MarketingButton
                    startIcon={<PaymentIcon />}
                    onClick={() => setCheckoutDialog(true)}
                    disabled={cart?.totalPrice === 0}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Proceed to Checkout
                  </MarketingButton>
                </Stack>
              </Box>
            )}
          </Stack>
        </MarketingCard>

        <Dialog open={checkoutDialog} onClose={() => setCheckoutDialog(false)} maxWidth="sm" fullWidth>
          <MarketingCard title="Payment Details" sx={{ m: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
            <Box component="form" onSubmit={handleCheckout}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <Select
                    value={paymentMethod}
                    onChange={({ target }) => setPaymentMethod(target.value)}
                    sx={{ borderRadius: 1, fontFamily: marketingFonts.body }}
                  >
                    <MenuItem value="Card">Online Card</MenuItem>
                    <MenuItem value="Wallet">Mobile Wallet</MenuItem>
                  </Select>
                </FormControl>

                {paymentMethod === "Wallet" && (
                  <MuiPhoneNumber
                    fullWidth
                    required
                    id="mobile"
                    label="Mobile Number"
                    name="mobile"
                    defaultCountry="eg"
                    preferredCountries={['eg']}
                    countryCodeEditable={false}
                    inputProps={{ minLength: 13 }}
                    disableAreaCodes
                    sx={{
                      "& label": { color: `${marketingColors.gray700} !important`, fontFamily: marketingFonts.body },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        fontFamily: marketingFonts.body,
                        "& fieldset": { borderColor: marketingColors.gray200 },
                        "&.Mui-focused fieldset": { borderColor: marketingColors.p700 },
                        borderRadius: "8px",
                      },
                    }}
                  />
                )}

                <Alert severity="info" sx={{ borderRadius: 1 }}>
                  <Typography sx={{ fontFamily: marketingFonts.body, fontSize: '0.875rem' }}>
                    Convenience Fees: {convenienceFees} EGP
                  </Typography>
                </Alert>

                <MarketingButton type="submit" fullWidth>
                  Complete Payment
                </MarketingButton>
              </Stack>
            </Box>
          </MarketingCard>
        </Dialog>

        <ActionDialog
          open={clearDialog}
          handleClose={() => setClearDialog(false)}
          handleAccept={handleClearCart}
          title="Clear Cart"
          content="Are you sure you want to remove all items from your cart?"
        />
      </motion.div>
    </MarketingPage>
  );
};

const mapStateToProps = (state) => ({
  cart: state?.auth?.cart,
  isLoading: state?.auth?.cart?.isLoading || state?.wait?.isLoading,
});

const mapDispatchToProps = { getCart, clearCart, removeFromCart, checkout };

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
